import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { UPSELL_COPY, UPSELL_MICROCOPY, UPSELL_SECONDARY, getPlans, fillCopy, fmtEur, startStandardCheckout } from "../lib/upsell";
import { Dialog, DialogContent } from "./ui/dialog";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

export function useStandardPlan() {
    const [std, setStd] = useState(null);
    useEffect(() => { getPlans().then((p) => setStd(p.standard)).catch(() => {}); }, []);
    return std;
}

export function StandardPriceBlock({ std, dark = false }) {
    const perAnalysis = std && std.monthly_limit > 0 ? std.price_eur / std.monthly_limit : null;
    return (
        <div className={`flex items-baseline gap-3 flex-wrap ${dark ? "text-white" : "text-gray-900"}`} data-testid="standard-price-block">
            <span className="text-[11px] font-bold tracking-[0.18em]">STANDARD</span>
            <span className="font-display-black text-2xl tabular-nums">{fmtEur(std?.price_eur ?? 19.9)}</span>
            <span className={`text-xs ${dark ? "text-slate-300" : "text-gray-500"}`}>/ mese · {std?.monthly_limit ?? 5} analisi al mese</span>
            {perAnalysis != null && (
                <span className={`text-[11px] ${dark ? "text-slate-400" : "text-gray-400"}`} data-testid="per-analysis-price">
                    circa {fmtEur(perAnalysis)} per analisi
                </span>
            )}
        </div>
    );
}

export function StandardCta({ variant, source, returnTo, className = "", dark = false }) {
    const std = useStandardPlan();
    const [loading, setLoading] = useState(false);
    const copy = UPSELL_COPY[variant];
    const go = async () => {
        setLoading(true);
        try {
            await startStandardCheckout(source, returnTo);
        } catch {
            toast.error("Impossibile aprire il checkout. Riprova.");
            setLoading(false);
        }
    };
    return (
        <button onClick={go} disabled={loading} data-testid={`standard-cta-${source}`}
                className={`h-12 px-6 rounded-xl bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] text-white text-sm font-bold hover:brightness-110 transition-all shadow-[0_6px_22px_rgba(79,90,245,0.35)] flex items-center justify-center gap-2 disabled:opacity-60 ${className}`}>
            {loading ? "Apertura checkout…" : fillCopy(copy.cta, std)} {!loading && <ArrowRight size={15} weight="bold" />}
        </button>
    );
}

// Blocco premium in fondo al risultato della PRIMA analisi (solo Free con analisi usata)
export function FirstAnalysisUpsell({ analysisStatus, auctionId }) {
    const [quota, setQuota] = useState(null);
    const std = useStandardPlan();
    const show = quota?.plan === "free" && (quota?.used || 0) >= 1 && analysisStatus === "completed";
    useEffect(() => { api.get("/account/quota").then((r) => setQuota(r.data)).catch(() => {}); }, []);
    useEffect(() => {
        if (show && !sessionStorage.getItem("pw_analysis_result")) {
            sessionStorage.setItem("pw_analysis_result", "1");
            trackEvent("paywall_viewed", "analysis_result");
        }
    }, [show]);
    if (!show) return null;
    const copy = UPSELL_COPY.analysis_result;
    return (
        <div className="mt-6 rounded-2xl border border-[#7C3AED]/35 bg-gradient-to-r from-[#141C33] to-[#0E1424] p-6 md:p-7" data-testid="first-analysis-upsell">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#A78BFA] mb-2">{copy.eyebrow}</div>
            <p className="text-sm text-slate-200 leading-relaxed max-w-2xl">{fillCopy(copy.body, std)}</p>
            <div className="mt-4"><StandardPriceBlock std={std} dark /></div>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <StandardCta variant="analysis_result" source="analysis_result" returnTo={auctionId ? `/auctions/${auctionId}` : null} />
                <span className="text-[11px] text-slate-400">{UPSELL_MICROCOPY}</span>
            </div>
        </div>
    );
}

// Paywall contestuale (dialog) per seconda analisi / trial scaduto
export function StandardPaywallDialog({ open, onClose, variant = "second_analysis_paywall", source, returnTo }) {
    const std = useStandardPlan();
    const copy = UPSELL_COPY[variant];
    useEffect(() => {
        if (open) trackEvent("paywall_viewed", source);
    }, [open, source]);
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md p-0 overflow-hidden border-[#7C3AED]/40 bg-gradient-to-b from-[#141C33] to-[#0E1424] text-white" data-testid="standard-paywall">
                <div className="p-6">
                    <div className="text-[10px] font-bold tracking-[0.2em] text-[#A78BFA]" data-testid="paywall-eyebrow">{copy.eyebrow}</div>
                    <p className="text-sm text-slate-200 mt-2 leading-relaxed">{fillCopy(copy.body, std)}</p>
                    <div className="mt-4"><StandardPriceBlock std={std} dark /></div>
                    {copy.features && (
                        <ul className="mt-3 space-y-1.5">
                            {copy.features.map((f) => (
                                <li key={f} className="flex items-center gap-2 text-[13px] text-slate-200">
                                    <CheckCircle size={15} weight="fill" className="text-emerald-400 shrink-0" /> {fillCopy(f, std)}
                                </li>
                            ))}
                        </ul>
                    )}
                    <StandardCta variant={variant} source={source} returnTo={returnTo} className="w-full mt-5" />
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] text-slate-400">{UPSELL_MICROCOPY}</span>
                        <Link to="/piano" data-testid="paywall-compare-plans" className="text-[11px] text-slate-300 underline hover:text-white">
                            {UPSELL_SECONDARY}
                        </Link>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
